// The checks the server makes on settings (sdk/CONTRACT.md, "Settings": a
// JSON Schema built from `defaults.settings`, additionalProperties false),
// made here first so a form says what is wrong before it is sent. Messages
// are in what the person sees: a ratio's bounds as percentages.

import type { ActionSetting, ResolvedSetting, SettingsMode } from '../types';
import { formatSettingValue, optionLabel } from './format';
import { canChange, resolveSettings } from './resolve';

/** Why a value is refused, or null when it is fine. */
export function validateValue(s: ResolvedSetting, value: unknown): string | null {
  if (value === undefined || value === null || (typeof value === 'string' && value === '' && s.type !== 'string')) {
    return 'Enter a value.';
  }
  switch (s.type) {
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) return 'Enter a number.';
      break;
    case 'integer':
      if (typeof value !== 'number' || !Number.isInteger(value)) return 'Enter a whole number.';
      break;
    case 'boolean':
      if (typeof value !== 'boolean') return 'Choose on or off.';
      break;
    case 'string':
      if (typeof value !== 'string') return 'Enter text.';
      break;
  }
  if (Array.isArray(s.enum) && s.enum.length > 0 && !s.enum.some((e) => e === value)) {
    return `Choose one of: ${s.enum.map(optionLabel).join(', ')}.`;
  }
  if (typeof value === 'number') {
    if (s.minimum != null && value < s.minimum) return `Must be at least ${formatSettingValue(s, s.minimum)}.`;
    if (s.maximum != null && value > s.maximum) return `Must be at most ${formatSettingValue(s, s.maximum)}.`;
  }
  return null;
}

/**
 * Every problem with the values a form in this mode would send, by setting:
 * a setting it may change with a bad value, and any key the action does not
 * have. The settings it may not change are shown and never sent, so they are
 * not checked. Empty when the values can be sent.
 */
export function validateSettings(
  settings: Record<string, ActionSetting> | null | undefined,
  values: Record<string, unknown>,
  mode: SettingsMode,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const all = resolveSettings(settings);
  const keys = new Set(all.map((s) => s.key));
  for (const s of all) {
    if (!canChange(s, mode)) continue;
    const problem = validateValue(s, values[s.key]);
    if (problem) errors[s.key] = problem;
  }
  for (const key of Object.keys(values)) {
    if (!keys.has(key)) errors[key] = 'This action has no such setting.';
  }
  return errors;
}
